import { useCallback, useEffect, useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Warning, FileDashed, Trash } from "@phosphor-icons/react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import OutsourceForm from "@/components/OutsourceForm";
import InhouseForm from "@/components/InhouseForm";
import SubmissionsTable, { fmtDT } from "@/components/SubmissionsTable";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function DriverPanel({ user, staff, defaultTab = "outsource" }) {
  const [subs, setSubs] = useState([]);
  const [drafts, setDrafts] = useState([]);
  const [inspToday, setInspToday] = useState(null);
  const [tab, setTab] = useState(defaultTab);
  const [resumeDraft, setResumeDraft] = useState(null);

  const refresh = useCallback(() => {
    api.get("/submissions").then((r) => setSubs(r.data)).catch(() => {});
    api.get("/submissions?drafts=1").then((r) => setDrafts(r.data)).catch(() => {});
    api.get("/inspections/today").then((r) => setInspToday(r.data)).catch(() => {});
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const resume = (d) => {
    setResumeDraft(d);
    setTab(d.type === "inhouse" ? "inhouse" : "outsource");
  };

  const removeDraft = async (d) => {
    if (!window.confirm(`Delete draft ${d.job_no}?`)) return;
    try {
      await api.delete(`/submissions/${d.id}`);
      toast.success("Draft deleted");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-5">
      {inspToday && !inspToday.done && (
        <div className="flex items-center gap-3 rounded-sm border border-amber-500/40 bg-amber-500/10 px-5 py-4" data-testid="inspection-warning">
          <Warning size={22} className="shrink-0 text-amber-400" />
          <p className="text-sm font-semibold text-amber-300">
            Daily vehicle inspection not yet submitted for today ({inspToday.date}). Go to the <b>Inspection</b> tab to upload 4 exterior photos + 1 odometer photo.
          </p>
        </div>
      )}
    <Tabs value={tab} onValueChange={(v) => { setTab(v); if (v === "outsource" || v === "inhouse") setResumeDraft(null); }} data-testid="driver-panel">
      <TabsList className="mb-6 flex-wrap rounded-sm border border-border bg-card">
        <TabsTrigger data-testid="tab-outsource" value="outsource" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Outsource Towing</TabsTrigger>
        <TabsTrigger data-testid="tab-inhouse" value="inhouse" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">Inhouse Referral</TabsTrigger>
        <TabsTrigger data-testid="tab-my-jobs" value="my-jobs" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">My Submissions</TabsTrigger>
        <TabsTrigger data-testid="tab-drafts" value="drafts" className="rounded-sm data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
          Drafts {drafts.length > 0 && <Badge variant="outline" className="ml-2 rounded-sm border-amber-500/50 text-[10px] font-bold text-amber-400">{drafts.length}</Badge>}
        </TabsTrigger>
      </TabsList>
      <TabsContent value="outsource">
        <OutsourceForm key={resumeDraft?.id || "new-outsource"} user={user} staff={staff} draft={resumeDraft?.type === "outsource" ? resumeDraft : null}
          onDone={() => { setResumeDraft(null); refresh(); }} />
      </TabsContent>
      <TabsContent value="inhouse">
        <InhouseForm key={resumeDraft?.id || "new-inhouse"} user={user} staff={staff} draft={resumeDraft?.type === "inhouse" ? resumeDraft : null}
          onDone={() => { setResumeDraft(null); refresh(); }} />
      </TabsContent>
      <TabsContent value="my-jobs">
        <SubmissionsTable submissions={subs} onRefresh={refresh} />
      </TabsContent>
      <TabsContent value="drafts">
        <div className="overflow-x-auto rounded-sm border border-border" data-testid="drafts-table">
          <Table>
            <TableHeader>
              <TableRow className="border-border hover:bg-transparent">
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Draft</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Type</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Details</TableHead>
                <TableHead className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Saved</TableHead>
                <TableHead></TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {drafts.map((d) => (
                <TableRow key={d.id} className="border-border hover:bg-muted/50" data-testid={`draft-row-${d.id}`}>
                  <TableCell className="font-mono text-sm font-bold text-amber-400">{d.job_no}</TableCell>
                  <TableCell><Badge variant="outline" className="rounded-sm font-bold uppercase tracking-wider">{d.type}</Badge></TableCell>
                  <TableCell className="max-w-[260px] truncate text-sm text-muted-foreground">
                    {d.type === "inhouse" ? (d.company || "—") : `${d.customer?.name || "—"} · ${(d.from_address || "").split(",")[0]} → ${(d.to_address || "").split(",")[0]}`}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">{fmtDT(d.submitted_at)}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button size="sm" data-testid={`draft-resume-${d.id}`} onClick={() => resume(d)}
                        className="rounded-sm bg-primary font-bold uppercase tracking-wider text-primary-foreground hover:bg-amber-400 hover:text-black">
                        <FileDashed size={14} className="mr-1" /> Resume
                      </Button>
                      <Button size="sm" variant="outline" data-testid={`draft-delete-${d.id}`} onClick={() => removeDraft(d)}
                        className="rounded-sm border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300">
                        <Trash size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {!drafts.length && (
                <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">
                  <FileDashed size={28} className="mx-auto mb-2 text-muted-foreground" />No saved drafts. Use "Save as Draft" on a job form to park it for later.
                </TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </TabsContent>
    </Tabs>
    </div>
  );
}
